export type GlobaleEnvironment = 'prd' | 'int'

export type GlobaleParams = {
  countryCode: string
  currencyCode: string
  isExternal: true
}

const STOREFRONT_SCRIPT_SRC: Record<GlobaleEnvironment, string> = {
  prd: 'https://web.global-e.com/Scripts/Merchants/globale.merchant.storefront.js',
  int: 'https://bglobale.com/Scripts/Merchants/globale.merchant.storefront.js',
}

const APP_SCRIPT_ORIGIN: Record<GlobaleEnvironment, string> = {
  prd: 'https://crossborder-integration.global-e.com',
  int: 'https://crossborder-integration-qa-int.bglobale.com',
}

export function resolveGlobaleEnvironment(value?: string | null): GlobaleEnvironment {
  return value === 'prd' ? 'prd' : 'int'
}

export function buildGlobaleParams(
  countryCode?: string | null,
  currencyCode?: string | null
): GlobaleParams | null {
  const country = (countryCode || '').trim().toUpperCase()
  const currency = (currencyCode || '').trim().toUpperCase()

  if (!/^[A-Z]{2}$/.test(country)) return null
  // EU is a region, not a country — it must be resolved before reaching here.
  if (country === 'EU') return null
  if (!/^[A-Z]{3}$/.test(currency)) return null

  return { countryCode: country, currencyCode: currency, isExternal: true }
}

export function serialiseGlobaleParams(params: GlobaleParams): string {
  const json = JSON.stringify(params).replace(/</g, '\\u003c')
  return `window.GLBE_PARAMS = ${json};`
}

export function globaleStorefrontScriptSrc(environment: GlobaleEnvironment): string {
  return STOREFRONT_SCRIPT_SRC[environment]
}

export function normaliseShopDomain(shopDomain?: string | null): string | null {
  const host = (shopDomain || '')
    .trim()
    .toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, '') // scheme
    .replace(/^\/\//, '') // protocol-relative
    .replace(/^[^/?#@]*@/, '') // userinfo
    .split(/[/?#]/)[0] // path, query, fragment
    .replace(/:\d+$/, '') // port
    .replace(/\.+$/, '') // trailing dots

  // Labels of alphanumerics and hyphens, at least one dot, no empty labels.
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(host)) return null

  return host
}

export function globaleAppScriptSrc(
  environment: GlobaleEnvironment,
  shopDomain?: string | null
): string | null {
  const host = normaliseShopDomain(shopDomain)

  if (!host) return null

  return `${APP_SCRIPT_ORIGIN[environment]}/resources/js/app?shop=${encodeURIComponent(host)}`
}

export function resolveGlobaleCountry(
  localeCountryCode?: string | null,
  geoCountryCode?: string | null,
  sameCurrencyCountryCodes: readonly string[] = []
): string | null {
  const locale = (localeCountryCode || '').trim().toUpperCase()

  if (/^[A-Z]{2}$/.test(locale) && locale !== 'EU') return locale
  if (locale !== 'EU') return null

  const geo = (geoCountryCode || '').trim().toUpperCase()
  const allowed = sameCurrencyCountryCodes.map((code) => code.toUpperCase())

  if (/^[A-Z]{2}$/.test(geo) && allowed.includes(geo)) return geo

  // The EU default, matching the Shopify store's own EU -> FR mapping.
  return allowed.includes('FR') ? 'FR' : null
}

export type GlobaleManagedMarket = {
  metafield?: { value?: string | null } | null
}

export function isGlobaleManagedMarket(market?: GlobaleManagedMarket | null): boolean {
  return market?.metafield?.value?.trim().toLowerCase() === 'true'
}
